// src/components/templates/diary/EnrolledCourseViewer.jsx
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, BookOpen, Play, CheckCircle, Clock, Award, ChevronRight,
  ChevronDown, ChevronUp, Star, MessageSquare, FileText, Video, HelpCircle
} from 'lucide-react';
import { BlockType } from '../../constants/learningConstants';
import ReactMarkdown from 'react-markdown';

const EnrolledCourseViewer = ({ bookId, courseId, user, enrollment, onUpdateEnrollment, onBack, diaryService }) => {
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [completedBlocks, setCompletedBlocks] = useState(new Set());
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    loadCourseData();
  }, [bookId, courseId]);

  const loadCourseData = async () => {
    setLoading(true);
    try {
      const [courseData, lessonsData] = await Promise.all([
        diaryService.getCourse(bookId, courseId),
        diaryService.getLessons(bookId, courseId)
      ]);

      setCourse(courseData);
      setLessons(lessonsData);

      if (lessonsData.length > 0) {
        setCurrentLesson(lessonsData[0]);
      }
    } catch (error) {
      console.error('Error loading course data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLessonSelect = (lesson, index) => {
    setCurrentLesson(lesson);
    setCurrentLessonIndex(index);
    setCurrentBlockIndex(0);
  };

  const handleBlockComplete = async (blockIndex) => {
    const newCompleted = new Set(completedBlocks);
    newCompleted.add(blockIndex);
    setCompletedBlocks(newCompleted);

    // If all blocks completed, mark lesson complete
    if (currentLesson && newCompleted.size === currentLesson.contentBlocks.length) {
      try {
        const completedLessons = [...(enrollment.completedLessons || [])];
        if (!completedLessons.includes(currentLesson.id)) {
          completedLessons.push(currentLesson.id);
        }
        
        const progress = (completedLessons.length / lessons.length) * 100;
        
        await onUpdateEnrollment({
          ...enrollment,
          completedLessons,
          progress,
          lastAccessedAt: new Date()
        });
      } catch (error) {
        console.error('Error updating progress:', error);
      }
    }
  };

  const handleNextBlock = () => {
    if (currentLesson && currentBlockIndex < currentLesson.contentBlocks.length - 1) {
      setCurrentBlockIndex(currentBlockIndex + 1);
    } else if (currentLessonIndex < lessons.length - 1) {
      // Move to next lesson
      const nextLesson = lessons[currentLessonIndex + 1];
      setCurrentLesson(nextLesson);
      setCurrentLessonIndex(currentLessonIndex + 1);
      setCurrentBlockIndex(0);
      setCompletedBlocks(new Set());
    }
  };

  const handlePreviousBlock = () => {
    if (currentBlockIndex > 0) {
      setCurrentBlockIndex(currentBlockIndex - 1);
    }
  };

  const handleAssessmentSubmit = async (blockIndex, answers) => {
    // Calculate score
    const block = currentLesson.contentBlocks[blockIndex];
    const questions = block.content?.questions || [];
    let correct = 0;

    questions.forEach((q, qIndex) => {
      if (answers[qIndex] === q.correctAnswer) {
        correct++;
      }
    });

    const score = (correct / questions.length) * 100;

    // Record result in enrollment
    try {
      const completedAssessments = enrollment.completedAssessments || {};
      completedAssessments[`${currentLesson.id}_${blockIndex}`] = {
        score,
        correct,
        total: questions.length,
        answers,
        completedAt: new Date()
      };

      await onUpdateEnrollment({
        ...enrollment,
        completedAssessments,
        lastAccessedAt: new Date()
      });

      // Mark block complete if passing
      if (score >= 70) {
        handleBlockComplete(blockIndex);
      }
    } catch (error) {
      console.error('Error recording assessment:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLesson) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <BookOpen className="w-16 h-16 text-gray-400 mb-4" />
        <p className="text-gray-600">No lessons available</p>
        <button onClick={onBack} className="mt-4 text-indigo-600 hover:text-indigo-700">
          Go Back
        </button>
      </div>
    );
  }

  const currentBlock = currentLesson.contentBlocks?.[currentBlockIndex];
  const isLastBlock = currentBlockIndex === (currentLesson.contentBlocks?.length || 0) - 1;
  const isLastLesson = currentLessonIndex === lessons.length - 1;

  return (
    <div className="h-full flex bg-gray-50">
      {/* Sidebar */}
      <div className={`${showSidebar ? 'w-80' : 'w-0'} transition-all bg-white border-r overflow-y-auto shrink-0`}>
        {showSidebar && (
          <div className="p-4 space-y-4">
            {/* Header */}
            <div>
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <h2 className="font-bold text-gray-900 line-clamp-2">{course?.title}</h2>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                {Math.round((course?.estimatedDuration || 0) / 60)}h
              </div>
            </div>

            {/* Progress */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Overall Progress</span>
                <span className="font-medium text-gray-900">{Math.round(enrollment?.progress || 0)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600"
                  style={{ width: `${enrollment?.progress || 0}%` }}
                />
              </div>
            </div>

            {/* Lessons List */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Lessons</h3>
              <div className="space-y-1">
                {lessons.map((lesson, index) => {
                  const isCompleted = enrollment?.completedLessons?.includes(lesson.id);
                  const isCurrent = index === currentLessonIndex;

                  return (
                    <button
                      key={lesson.id}
                      onClick={() => handleLessonSelect(lesson, index)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        isCurrent
                          ? 'bg-indigo-50 border-2 border-indigo-200'
                          : 'hover:bg-gray-50 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-1 shrink-0 ${isCompleted ? 'text-green-500' : 'text-gray-400'}`}>
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <Play className="w-5 h-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm line-clamp-2">
                            {lesson.title}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <Clock className="w-3 h-3" />
                            {lesson.duration}m
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Lesson Header */}
        <div className="bg-white border-b p-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{currentLesson.title}</h1>
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              {showSidebar ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
          </div>
          {currentLesson.description && (
            <p className="text-gray-600">{currentLesson.description}</p>
          )}

          {/* Block Progress */}
          {currentLesson.contentBlocks && currentLesson.contentBlocks.length > 0 && (
            <div className="mt-4 flex items-center gap-2">
              {currentLesson.contentBlocks.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-2 flex-1 rounded-full ${
                    idx === currentBlockIndex
                      ? 'bg-indigo-600'
                      : completedBlocks.has(idx)
                      ? 'bg-green-500'
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentBlock && (
            <ContentBlockRenderer
              block={currentBlock}
              blockIndex={currentBlockIndex}
              onComplete={() => handleBlockComplete(currentBlockIndex)}
              onAssessmentSubmit={(answers) => handleAssessmentSubmit(currentBlockIndex, answers)}
              assessmentAnswers={assessmentAnswers}
              setAssessmentAnswers={setAssessmentAnswers}
            />
          )}
        </div>

        {/* Navigation Footer */}
        <div className="bg-white border-t p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousBlock}
              disabled={currentBlockIndex === 0 && currentLessonIndex === 0}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <div className="text-sm text-gray-600">
              Block {currentBlockIndex + 1} of {currentLesson.contentBlocks?.length || 0}
            </div>

            <button
              onClick={handleNextBlock}
              disabled={isLastBlock && isLastLesson}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLastBlock && !isLastLesson ? 'Next Lesson' : isLastBlock && isLastLesson ? 'Complete' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTENT BLOCK RENDERER ====================

const ContentBlockRenderer = ({ block, blockIndex, onComplete, onAssessmentSubmit, assessmentAnswers, setAssessmentAnswers }) => {
  const [submitted, setSubmitted] = useState(false);
  const [localAnswers, setLocalAnswers] = useState({});

  const handleAnswerChange = (questionIndex, answer) => {
    setLocalAnswers(prev => ({ ...prev, [questionIndex]: answer }));
  };

  const handleSubmit = () => {
    onAssessmentSubmit(localAnswers);
    setSubmitted(true);
  };

  if (block.type === BlockType.TEXT) {
    return (
      <div className="prose max-w-none">
        <ReactMarkdown>{block.content}</ReactMarkdown>
        <button
          onClick={onComplete}
          className="mt-6 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Mark as Complete
        </button>
      </div>
    );
  }

  if (block.type === BlockType.VIDEO) {
    return (
      <div className="space-y-4">
        <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
          {block.content.includes('youtube.com') || block.content.includes('youtu.be') ? (
            <iframe
              src={block.content}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={block.content} controls className="w-full h-full" />
          )}
        </div>
        <button
          onClick={onComplete}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Mark as Complete
        </button>
      </div>
    );
  }

  if (block.type === BlockType.QUIZ) {
    const questions = block.content?.questions || [];

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-1">Quiz Assessment</h3>
          <p className="text-blue-700 text-sm">
            Answer all questions and submit. You need 70% to pass.
          </p>
        </div>

        {questions.map((question, qIndex) => (
          <div key={qIndex} className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">
              {qIndex + 1}. {question.question}
            </h4>

            <div className="space-y-2">
              {question.options.map((option, optIndex) => (
                <label
                  key={optIndex}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                    localAnswers[qIndex] === optIndex
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${qIndex}`}
                    checked={localAnswers[qIndex] === optIndex}
                    onChange={() => handleAnswerChange(qIndex, optIndex)}
                    disabled={submitted}
                    className="text-indigo-600"
                  />
                  <span className="text-gray-900">{option}</span>
                  {submitted && optIndex === question.correctAnswer && (
                    <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
                  )}
                </label>
              ))}
            </div>

            {submitted && question.explanation && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">{question.explanation}</p>
              </div>
            )}
          </div>
        ))}

        {!submitted ? (
          <button
            onClick={handleSubmit}
            disabled={Object.keys(localAnswers).length !== questions.length}
            className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit Quiz
          </button>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-green-700 font-medium">Quiz submitted successfully!</p>
            <button
              onClick={onComplete}
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="text-gray-500">
      Unsupported content block type
    </div>
  );
};

export default EnrolledCourseViewer;